import { useState } from 'react'
import { useAdminAffiliate, useAdminAffiliateList, useUpdateAnyAffiliate } from 'src/hooks/useAdminAffiliates'
import { AdminAffiliateRow } from 'src/types/Affiliate'
import { formatDay } from 'src/utils/affiliateFormat'
import { notify } from 'src/utils/notify'
import { errorMessage } from '../affiliate/labels'
import {
    Card,
    CardTitle,
    DangerButton,
    ErrorText,
    InlineSpinner,
    LinkButton,
    Muted,
    Pager,
    PrimaryButton,
    Row,
    Spacer,
} from '../affiliate/ui'
import { ApplicationDetails, InlineLink, SmallButtonStyle, useClampPage, userLabel } from './shared'

const LazyApplication = ({ affiliateId }: { affiliateId: string }) => {
    const { data, isLoading, error } = useAdminAffiliate(affiliateId)
    if (isLoading) return <InlineSpinner />
    if (error) return <ErrorText>{errorMessage(error)}</ErrorText>
    if (!data) return null
    return <ApplicationDetails application={data.application} />
}

const ApplicationCard = ({
    row,
    update,
}: {
    row: AdminAffiliateRow
    update: ReturnType<typeof useUpdateAnyAffiliate>
}) => {
    const [open, setOpen] = useState(false)
    const busy = update.isPending && update.variables?.id === row.id

    const decide = (status: 'active' | 'rejected') => {
        if (status === 'rejected' && !window.confirm(`Reject the application from ${userLabel(row.user)}?`)) return
        update.mutate(
            { id: row.id, body: { status } },
            {
                onSuccess: () => notify(status === 'active' ? 'Application approved.' : 'Application rejected.', 'success'),
                onError: (err) => notify(errorMessage(err), 'error'),
            }
        )
    }

    return (
        <Card style={{ background: 'rgba(255,255,255,0.04)' }}>
            <Row>
                <div style={{ minWidth: 0 }}>
                    <InlineLink to={`/admin/affiliates/${row.id}`}>{row.code}</InlineLink>
                    <Muted style={{ overflowWrap: 'anywhere' }}>
                        {userLabel(row.user)}, applied {formatDay(row.createdAt)}
                    </Muted>
                </div>
                <Spacer />
                <LinkButton onClick={() => setOpen((value) => !value)}>
                    {open ? 'Hide details' : 'Show details'}
                </LinkButton>
            </Row>
            {open && <LazyApplication affiliateId={row.id} />}
            <Row>
                <PrimaryButton style={SmallButtonStyle} disabled={busy} onClick={() => decide('active')}>
                    Approve
                </PrimaryButton>
                <DangerButton style={SmallButtonStyle} disabled={busy} onClick={() => decide('rejected')}>
                    Reject
                </DangerButton>
            </Row>
        </Card>
    )
}

const ApplicationsTab = () => {
    const [page, setPage] = useState(1)
    const { data, isLoading, error, isPlaceholderData } = useAdminAffiliateList({ status: 'applied', page })
    useClampPage(page, data?.totalPages, setPage, isPlaceholderData)
    const update = useUpdateAnyAffiliate()

    return (
        <Card>
            <Row>
                <CardTitle>Applications</CardTitle>
                {data && <Muted>{data.totalResults} waiting</Muted>}
            </Row>
            {isLoading ? (
                <InlineSpinner />
            ) : error ? (
                <ErrorText>{errorMessage(error)}</ErrorText>
            ) : !data || data.results.length === 0 ? (
                <Muted>No pending applications.</Muted>
            ) : (
                <>
                    {data.results.map((row) => (
                        <ApplicationCard key={row.id} row={row} update={update} />
                    ))}
                    <Pager page={data.page} totalPages={data.totalPages} onChange={setPage} />
                </>
            )}
        </Card>
    )
}

export default ApplicationsTab
